import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import type { PlacementPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  DataTable, applyClientListParams,
  type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import { Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { usePinFilters } from "@/lib/pins-context";
import { PinFilterBar } from "@/components/pin-filter-bar";
import { PreviewPane, PreviewField, PreviewPaneToggle } from "@/components/preview-pane";

const statusColors: Record<string, string> = {
  reserved: "bg-yellow-100 text-yellow-800",
  active: "bg-green-100 text-green-800",
  expired: "bg-gray-100 text-gray-600",
  cancelled: "bg-red-100 text-red-800",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[status] ?? ""}`}>
      {status}
    </span>
  );
}

export default function PlacementsPage() {
  const [selected, setSelected] = useState<PlacementPublic | null>(null);
  const [pinned, setPinned] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [tableState, setTableState] = useState<"loading" | "error" | "empty" | "ready">("loading");
  const { activeOrgIds, activeSchoolIds, activeSponsorIds } = usePinFilters();

  const pinKey = `${activeOrgIds.join(",")}|${activeSchoolIds.join(",")}|${activeSponsorIds.join(",")}`;
  const hasPinFilter =
    activeOrgIds.length > 0 || activeSchoolIds.length > 0 || activeSponsorIds.length > 0;

  async function handleDelete(placementId: string) {
    if (!confirm("Delete this placement? This action cannot be undone.")) return;
    try {
      await api.deletePlacement(placementId);
      if (selected?.id === placementId) setSelected(null);
      setRefreshTick((t) => t + 1); // bump fetchKey -> DataTable refetches
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete placement");
    }
  }

  // The /placements endpoint does not model search/sort/pagination or the
  // org/school/sponsor pin filter, so we fetch the (bounded) full set and apply
  // all of it client-side via applyClientListParams.
  const fetcher = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: PlacementPublic[]; total: number }> => {
      const res = await api.getPlacements();
      const scoped = hasPinFilter
        ? res.placements.filter((p) =>
            activeOrgIds.includes(p.organization_id) ||
            activeSchoolIds.includes(p.school_id) ||
            activeSponsorIds.includes(p.sponsor_id),
          )
        : res.placements;
      return applyClientListParams(scoped, params, {
        searchFields: (p) => [p.sponsor_name, p.ad_space_name, p.school_name, p.court_name, p.status],
        sortAccessors: {
          sponsor: (p) => p.sponsor_name,
          ad_space: (p) => p.ad_space_name,
          school: (p) => p.school_name,
          period: (p) => p.start_date,
          status: (p) => p.status,
        },
        defaultSort: "sponsor",
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed via fetchKey
    [pinKey],
  );

  const columns: DataTableColumn<PlacementPublic>[] = [
    { key: "sponsor", header: "Sponsor", sortable: true, className: "font-medium", render: (p) => p.sponsor_name },
    { key: "ad_space", header: "Ad Space", sortable: true, render: (p) => p.ad_space_name },
    { key: "court", header: "Court", className: "text-muted-foreground", render: (p) => p.court_name },
    { key: "school", header: "School", sortable: true, className: "text-muted-foreground", render: (p) => p.school_name },
    {
      key: "period", header: "Period", sortable: true, className: "text-muted-foreground",
      render: (p) => `${p.start_date} -- ${p.end_date}`,
    },
    { key: "status", header: "Status", sortable: true, render: (p) => <StatusBadge status={p.status} /> },
    {
      key: "actions", header: "", headerClassName: "w-24",
      render: (p) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <Link to={`/placements/${p.id}`} className={buttonVariants({ variant: "ghost", size: "sm" })}>
            <Eye className="h-4 w-4" />
          </Link>
          {p._actions.can_edit && (
            <Link to={`/placements/${p.id}/edit`} className={buttonVariants({ variant: "ghost", size: "sm" })}>
              <Pencil className="h-4 w-4" />
            </Link>
          )}
          {p._actions.can_delete && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-full gap-0" data-page="placements-list" data-state={tableState}>
      <div className="min-w-0 flex-1 flex flex-col gap-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-semibold">Placements</h1>
          <div className="flex gap-2">
            <Link to="/placements/new" className={buttonVariants()} data-action="create">
              <Plus className="mr-2 h-4 w-4" />
              Add Placement
            </Link>
            <PreviewPaneToggle pinned={pinned} onTogglePin={() => setPinned((p) => !p)} />
          </div>
        </div>

        {error && <p className="text-sm text-destructive" data-error>{error}</p>}

        <PinFilterBar entityType="Organization" />
        <PinFilterBar entityType="School" />
        <PinFilterBar entityType="Sponsor" />

        <div data-region="results">
          <DataTable
            columns={columns}
            fetcher={fetcher}
            fetchKey={`${pinKey}:${refreshTick}`}
            rowKey={(p) => p.id}
            rowProps={(p) => ({ "data-item": "placement", "data-item-id": p.id })}
            searchPlaceholder="Search placements..."
            defaultSort="sponsor"
            emptyMessage="No placements yet."
            onRowClick={(p) => setSelected(p)}
            rowClassName={(p) => (selected?.id === p.id ? "bg-muted" : undefined)}
            onStateChange={setTableState}
          />
        </div>
      </div>
      <PreviewPane pinned={pinned} onTogglePin={() => setPinned((p) => !p)} title="Placement">
        {selected && (
          <>
            <PreviewField label="Sponsor">{selected.sponsor_name}</PreviewField>
            <PreviewField label="Ad Space">{selected.ad_space_name}</PreviewField>
            <PreviewField label="Court">{selected.court_name}</PreviewField>
            <PreviewField label="School">{selected.school_name}</PreviewField>
            <PreviewField label="Period">{selected.start_date} -- {selected.end_date}</PreviewField>
            <PreviewField label="Status"><StatusBadge status={selected.status} /></PreviewField>
          </>
        )}
      </PreviewPane>
    </div>
  );
}
